// src/pages/AdminMlmBonusLevels.js
import { useEffect, useState } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'
import DeleteButton from '../components/DeleteButtonModal'

const emptyForm = {
  fuel_type: 'fuel',
  level: '',
  max_count: '',
  bonus_per_unit: '',
}

const AdminMlmBonusLevels = () => {
  const [levels, setLevels] = useState([])
  const [form, setForm] = useState(emptyForm)

  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState(emptyForm)

  const fetchData = async () => {
    const res = await fetchWithAuth('/admin/mlm-bonus-levels')
    const data = await res.json()

    data.sort((a, b) => a.id - b.id)
    setLevels(data)
  }

  const buildPayload = (source) => ({
    ...source,
    level: Number(source.level),
    max_count: Number(source.max_count),
    bonus_per_unit: Number(source.bonus_per_unit),
  })

  const handleAdd = async () => {
    const payload = buildPayload(form)

    const res = await fetchWithAuth(
      '/admin/mlm-bonus-levels',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    )

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err.message || 'Chyba ukladania')
      return
    }

    setForm(emptyForm)
    fetchData()
  }

  const handleEdit = (l) => {
    setEditId(l.id)

    setEditForm({
      fuel_type: l.fuel_type ?? 'fuel',
      level: String(l.level ?? ''),
      max_count: String(l.max_count ?? ''),
      bonus_per_unit: String(l.bonus_per_unit ?? ''),
    })
  }

  const handleCancelEdit = () => {
    setEditId(null)
    setEditForm(emptyForm)
  }

  const handleSaveEdit = async (id) => {
    const payload = buildPayload(editForm)

    const res = await fetchWithAuth(
      `/admin/mlm-bonus-levels/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    )

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err.message || 'Chyba ukladania')
      return
    }

    setEditId(null)
    setEditForm(emptyForm)
    fetchData()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Zmazať bonusovú úroveň?')) return

    await fetchWithAuth(
      `/admin/mlm-bonus-levels/${id}`,
      {
        method: 'DELETE',
      }
    )

    fetchData()
  }

  useEffect(() => {
    fetchData()
  }, [])

  const input = (label, key, type = 'text') => (
    <div style={{ marginBottom: 6 }}>
      <label>
        <strong>{label}:</strong>
        <br />

        <input
          type={type}
          value={form[key]}
          onChange={(e) =>
            setForm({
              ...form,
              [key]: e.target.value,
            })
          }
          style={{ width: 200 }}
        />
      </label>
    </div>
  )

  const editInput = (key, type = 'text') => (
    <input
      type={type}
      value={editForm[key]}
      onChange={(e) =>
        setEditForm({
          ...editForm,
          [key]: e.target.value,
        })
      }
      style={{
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  )

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h3
        style={{
          fontSize: 18,
          marginTop: 0,
          marginBottom: 10,
        }}
      >
        Správa MLM bonusov
      </h3>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
        }}
      >
        <div style={{ marginBottom: 6 }}>
          <label>
            <strong>Typ paliva:</strong>
            <br />

            <select
              value={form.fuel_type}
              onChange={(e) =>
                setForm({
                  ...form,
                  fuel_type: e.target.value,
                })
              }
              style={{ width: 200 }}
            >
              <option value="fuel">
                fuel
              </option>

              <option value="electric">
                electric
              </option>
            </select>
          </label>
        </div>

        {input(
          'Úroveň',
          'level',
          'number'
        )}

        {input(
          'Max počet členov',
          'max_count',
          'number'
        )}

        {input(
          'Bonus na jednotku',
          'bonus_per_unit',
          'number'
        )}
      </div>

      <button
        onClick={handleAdd}
        style={{
          marginTop: 10,
          alignSelf: 'flex-start',
        }}
      >
        Pridať
      </button>

      <hr
        style={{
          width: '100%',
        }}
      />

      <div
        style={{
          border: '1px solid #c7c7c7',
          borderRadius: 10,
          background: '#fff',
          flex: 1,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr
              style={{
                background: '#c8e1e1',
              }}
            >
              <th
                style={{
                  ...headerCell,
                  width: 145,
                  whiteSpace: 'nowrap',
                }}
              >
                Akcie
              </th>

              <th style={{ ...headerCell, width: 40 }}>
                ID
              </th>

              <th style={headerCell}>
                Typ
              </th>

              <th style={headerCell}>
                Úroveň
              </th>

              <th style={headerCell}>
                Max počet
              </th>

              <th style={headerCell}>
                Bonus
              </th>
            </tr>
          </thead>

          <tbody>
            {levels.map((l) => {
              const isEditing = editId === l.id

              return (
                <tr key={l.id}>
                  <td
                    style={{
                      ...bodyCell,
                      whiteSpace: 'nowrap',
                      paddingLeft: 6,
                      paddingRight: 6,
                    }}
                  >
                    {isEditing ? (
                      <>
                        <button
                          onClick={() =>
                            handleSaveEdit(l.id)
                          }
                        >
                          Uložiť
                        </button>

                        <button
                          onClick={handleCancelEdit}
                        >
                          Zrušiť
                        </button>
                      </>
                    ) : (
                      <>
                        <EditButton
                          onClick={() =>
                            handleEdit(l)
                          }
                        />

                        <DeleteButton
                          onClick={() =>
                            handleDelete(l.id)
                          }
                        />
                      </>
                    )}
                  </td>

                  <td style={bodyCell}>
                    {l.id}
                  </td>

                  <td style={bodyCell}>
                    {isEditing ? (
                      <select
                        value={editForm.fuel_type}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            fuel_type: e.target.value,
                          })
                        }
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <option value="fuel">
                          fuel
                        </option>

                        <option value="electric">
                          electric
                        </option>
                      </select>
                    ) : (
                      l.fuel_type
                    )}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('level', 'number')
                      : l.level}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('max_count', 'number')
                      : l.max_count}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('bonus_per_unit', 'number')
                      : l.bonus_per_unit}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminMlmBonusLevels

const headerCell = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 700,
  fontSize: 13,
}

const bodyCell = {
  textAlign: 'left',
  padding: '10px 12px',
  borderTop: '1px solid #ececec',
  fontSize: 13,
}